import type { ComponentType, ReactNode } from 'react'
import { Fragment, useEffect, useState } from 'react'
import { useAuth } from '@/components/auth/AuthProvider'
import { useSettingsEmbedded } from '@/components/shared/settings/settings-embed'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { EditableModuleAvatar } from '@/components/ui/editable-module-avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { useLayout } from '@/contexts/LayoutContext'
import { useScreenWidth } from '@/hooks/useScreenWidth'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'

import { ModuleTypeEnum } from '@scrambl/shared'

import type { DetailHeaderSlots } from './DetailHeader'
import { DetailHeaderTitle } from './DetailHeader'
import { PageTabs, TabItem } from './PageTabs'

export interface BreadcrumbItem {
  label: string
  href?: string
  current?: boolean
}

type ContainerWidth = 'default' | 'wide' | 'adaptive' | 'full'

interface PageHeaderProps {
  title: string | ReactNode
  titleSuffix?: string
  description?: ReactNode
  greeting?: boolean
  children?: ReactNode
  className?: string
  actions?: ReactNode
  /**
   * The page's overflow ("⋯") menu. Rendered after the actions on wide
   * screens; on phones it stays top right beside the title while the other
   * actions take the next row.
   */
  menu?: ReactNode
  tabs?: TabItem[]
  breadcrumbs?: BreadcrumbItem[]
  fixed?: boolean
  condensed?: boolean
  /**
   * Scroll-driven collapse (set by PageContainer once the content scrolls).
   * Uses the condensed layout, drops breadcrumbs, hero and strip, and on wide
   * screens folds the tabs into the title row, so the header is one line.
   */
  collapsed?: boolean
  /**
   * Drops the divider under the header so it reads as part of the page's own
   * surface rather than a chrome bar. For open, dashboard-like surfaces
   * (Today) where a rule across the top is the only thing that looks like a
   * frame.
   */
  flat?: boolean
  /** Icon component to display before the title */
  icon?: ComponentType<{ className?: string; weight?: string }>
  moduleType?: ModuleTypeEnum
  entityData?: {
    id?: string
    name?: string
    logoUrl?: string
  }
  onEntityClick?: () => void
  /** Callback for logo upload - when provided, avatar becomes editable for users with MANAGE permission */
  onLogoChange?: (file: File | null) => Promise<void>
  /** Override for full width layout - passed from PageContainer */
  fullWidth?: boolean
  /** Override for no padding - passed from PageContainer */
  noPadding?: boolean
  /** Override for container width - passed from PageContainer */
  containerWidth?: ContainerWidth
  /** Back link for navigation */
  backLink?: {
    label: string
    href: string
  }
  /** Loading state - shows skeleton placeholders */
  isLoading?: boolean
  /**
   * Title scale. Every app page is 20 px/600; `display` (32 px) is only for
   * hub landing pages with no data table, such as the Settings hub and the
   * Admin landing (design audit S6).
   */
  titleSize?: 'default' | 'display'
  /** Record page slots (crumb, avatar, name, status, star, follow, primary, overflow); see DetailHeader.tsx (design audit S7). */
  detail?: DetailHeaderSlots
  /** Decorative layer rendered absolutely behind the header block (title row, hero, tabs). Must be non-interactive. */
  background?: ReactNode
  /** Full-width hero content rendered inside the header block, between the title row and the tabs — sits on top of `background` (e.g. an org cover identity row) */
  hero?: ReactNode
  /** A row of its own between the title row (or hero) and the tabs, inside the header gutter — e.g. a record's stage gauge. */
  strip?: ReactNode
}

export function getGreeting(hours = new Date().getHours()): string {
  if (hours < 12) return 'Good morning'
  if (hours < 17) return 'Good afternoon'
  return 'Good evening'
}

function PageHeaderSkeleton({
  condensed,
  hasAvatar,
  hasDescription,
  hasActions,
  hasBreadcrumbs,
  isDisplay = false,
}: {
  condensed: boolean
  hasAvatar: boolean
  hasDescription: boolean
  hasActions: boolean
  hasBreadcrumbs: boolean
  isDisplay?: boolean
}) {
  if (condensed) {
    return (
      <div className="space-y-1">
        {/* First line: Breadcrumbs skeleton */}
        {hasBreadcrumbs && (
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-4" variant="circular" />
            <Skeleton className="h-4 w-24" />
          </div>
        )}

        {/* Second line: Title and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {hasAvatar && <Skeleton className="h-10 w-10" variant="circular" />}
            <Skeleton className="h-6 w-48" />
          </div>
          {hasActions && (
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-24 rounded-full" />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
      <div className="flex-1">
        {/* Breadcrumbs skeleton */}
        {hasBreadcrumbs && (
          <div className="mb-4 flex items-center gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-4" variant="circular" />
            <Skeleton className="h-5 w-32" />
          </div>
        )}

        {/* Title section skeleton */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {hasAvatar && <Skeleton className="h-10 w-10" variant="circular" />}
            <Skeleton className={isDisplay ? 'h-8 w-64' : 'h-6 w-64'} />
          </div>

          {/* Description skeleton */}
          {hasDescription && (
            <div className="max-w-3xl space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          )}
        </div>
      </div>

      {/* Actions skeleton */}
      {hasActions && (
        <div className="flex flex-shrink-0 items-center gap-2">
          <Skeleton className="h-9 w-28 rounded-full" />
          <Skeleton className="h-9 w-9 rounded-full" />
        </div>
      )}
    </div>
  )
}

// The scroll-collapsed header is one short line (the inline tabs' underline
// sits on its bottom edge): the 40px logo filled it edge to edge, so it steps
// down to 24px there.
type HeaderAvatarSize = 'xs' | 'md'

function renderTitleWithAvatar(
  title: string | ReactNode,
  titleSuffix?: string,
  moduleType?: ModuleTypeEnum,
  entityData?: { id?: string; name?: string; logoUrl?: string },
  className?: string,
  onEntityClick?: () => void,
  onLogoChange?: (file: File | null) => Promise<void>,
  Icon?: ComponentType<{ className?: string; weight?: string }>,
  avatarSize: HeaderAvatarSize = 'md',
): ReactNode {
  // Rich detail titles often include badges and action controls. They should
  // still receive the module avatar just like plain string titles do.
  if (
    moduleType &&
    entityData &&
    (moduleType === ModuleTypeEnum.ENTITY ||
      moduleType === ModuleTypeEnum.PROJECT)
  ) {
    return (
      <div className="flex items-center gap-3">
        <EditableModuleAvatar
          entity={entityData}
          moduleType={moduleType}
          size={avatarSize}
          editable={!!onLogoChange}
          onLogoChange={onLogoChange}
          onClick={onEntityClick}
        />
        <h1 className={className}>
          {title}
          {titleSuffix &&
            (onEntityClick ? (
              <button
                onClick={onEntityClick}
                className="ml-2 cursor-pointer font-normal text-muted-foreground transition-colors hover:text-primary hover:underline"
              >
                {titleSuffix}
              </button>
            ) : (
              <span className="ml-2 font-normal text-muted-foreground">
                {titleSuffix}
              </span>
            ))}
        </h1>
      </div>
    )
  }

  // Return title with optional icon
  if (typeof title === 'string') {
    if (Icon) {
      return (
        <div className="flex items-center gap-2.5">
          <Icon className="h-5 w-5 text-muted-foreground/70" />
          <h1 className={className}>
            {title}
            {titleSuffix && (
              <span className="ml-2 font-normal text-muted-foreground">
                {titleSuffix}
              </span>
            )}
          </h1>
        </div>
      )
    }
    return (
      <h1 className={className}>
        {title}
        {titleSuffix && (
          <span className="ml-2 font-normal text-muted-foreground">
            {titleSuffix}
          </span>
        )}
      </h1>
    )
  }

  // ReactNode titles keep heading semantics: the h1 is the page's landmark
  // whether or not the title carries badges or controls.
  if (Icon) {
    return (
      <div className="flex items-center gap-2.5">
        <Icon className="h-5 w-5 text-muted-foreground/70" />
        <h1 className={className}>{title}</h1>
      </div>
    )
  }

  return <h1 className={className}>{title}</h1>
}

/**
 * The one app page title: 20 px/600 at every width (design audit S6). Phones
 * get two lines before the ellipsis — a single line there cuts most record
 * names down to a word or two.
 */
export const PAGE_TITLE_CLASSES =
  'text-xl font-semibold tracking-tight text-foreground/95 truncate text-consistent max-sm:line-clamp-2 max-sm:whitespace-normal max-sm:break-words'

function renderDetailTitle(
  detail: DetailHeaderSlots,
  moduleType?: ModuleTypeEnum,
  entityData?: { id?: string; name?: string; logoUrl?: string },
  onEntityClick?: () => void,
  onLogoChange?: (file: File | null) => Promise<void>,
  titleClassName: string = PAGE_TITLE_CLASSES,
  avatarSize: HeaderAvatarSize = 'md',
): ReactNode {
  const avatar =
    moduleType &&
    entityData &&
    (moduleType === ModuleTypeEnum.ENTITY ||
      moduleType === ModuleTypeEnum.PROJECT) ? (
      <EditableModuleAvatar
        entity={entityData}
        moduleType={moduleType}
        size={avatarSize}
        editable={!!onLogoChange}
        onLogoChange={onLogoChange}
        onClick={onEntityClick}
      />
    ) : (
      detail.avatar
    )
  return (
    <DetailHeaderTitle
      slots={detail}
      avatar={avatar}
      titleClassName={titleClassName}
    />
  )
}

export function PageHeader({
  title,
  titleSuffix,
  description,
  greeting = false,
  children,
  className,
  actions,
  menu,
  tabs,
  breadcrumbs,
  fixed = false,
  condensed = false,
  collapsed = false,
  flat = false,
  icon: Icon,
  moduleType,
  entityData,
  onEntityClick,
  onLogoChange,
  fullWidth: propFullWidth,
  noPadding: propNoPadding,
  containerWidth: propContainerWidth,
  backLink,
  isLoading = false,
  titleSize = 'default',
  detail,
  background,
  hero,
  strip,
}: PageHeaderProps) {
  const { user } = useAuth()
  const { formatDate } = useUserTimezone()
  const [now, setNow] = useState(() => new Date())
  const firstName = user?.firstName || user?.email?.split('@')[0] || 'there'
  const currentHour = Number(formatDate(now, 'H'))
  const greetingText = getGreeting(
    Number.isFinite(currentHour) ? currentHour : now.getHours(),
  )

  useEffect(() => {
    if (!greeting) return

    const updateNow = () => setNow(new Date())
    updateNow()

    const interval = window.setInterval(updateNow, 60_000)
    return () => window.clearInterval(interval)
  }, [greeting])

  // Get layout options and screen info for adaptive width
  const { layoutOptions } = useLayout()
  const { isUltrawide, isLargeDesktop, category } = useScreenWidth()
  const isEmbedded = useSettingsEmbedded()

  // A lone tab is not navigation — it only restates the page (Forms' single
  // "All Forms" tab), so the tab bar renders for two or more tabs only.
  const hasTabs = !!tabs && tabs.length > 1

  // Hosted inside the Settings shell: the shell owns the page title and
  // breadcrumb, so the header shrinks to a section heading with the page's
  // actions and tabs (the same weight SettingsContent gives a section).
  if (isEmbedded) {
    return (
      <div className={cn('mb-6 space-y-4', className)}>
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start sm:gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {(actions || menu) && (
            <div className="flex flex-shrink-0 items-center gap-2">
              {actions}
              {menu}
            </div>
          )}
        </div>
        {children}
        {hasTabs && <PageTabs tabs={tabs} />}
      </div>
    )
  }

  // Props take precedence over context, then use defaults
  const effectiveFullWidth = propFullWidth ?? layoutOptions.fullWidth ?? false
  const effectiveNoPadding = propNoPadding ?? layoutOptions.noPadding ?? false
  const effectiveContainerWidth =
    propContainerWidth ?? layoutOptions.containerWidth ?? 'default'

  // Helper function to get container width classes (copied from PageContainer)
  const getContainerWidthClasses = (width: string, noPadding: boolean) => {
    // For header, always include some padding for readability
    // Even with noPadding, we keep minimal padding in the header
    const paddingClasses = noPadding
      ? 'px-4 sm:px-6 md:px-6 lg:px-8'
      : 'px-4 sm:px-6 md:px-8 lg:px-12'

    switch (width) {
      case 'full':
        return `${paddingClasses} w-full text-left min-w-0`
      case 'adaptive':
        // Adaptive based on actual window size (not screen size)
        if (isUltrawide) {
          // Very wide window (2200px+)
          return `max-w-[1800px] ${paddingClasses} min-w-0`
        } else if (isLargeDesktop) {
          // Large window (1600px+)
          return `max-w-[1600px] ${paddingClasses} min-w-0`
        } else if (category === 'medium') {
          // Medium window (1200-1599px)
          return `max-w-[1400px] ${paddingClasses} min-w-0`
        } else {
          // Small window (<1200px)
          return `max-w-[1200px] ${paddingClasses} min-w-0`
        }
      case 'wide':
        // Static wide width
        return `max-w-[1600px] ${paddingClasses} min-w-0`
      case 'default':
      default:
        return `max-w-[1400px] ${paddingClasses} min-w-0`
    }
  }

  // For settings pages, always use default width
  // For fullWidth, use 'full' containerWidth
  // Otherwise use the specified containerWidth
  const finalContainerWidth = effectiveFullWidth
    ? 'full'
    : effectiveContainerWidth

  const containerClasses = getContainerWidthClasses(
    finalContainerWidth,
    effectiveNoPadding,
  )

  const isCondensed = condensed || collapsed
  // Collapsed on lg+: the tabs move up between the title and the actions and
  // the tab row below is hidden, leaving a single header line.
  const hasInlineTabs = collapsed && hasTabs
  const condensedTitleClasses = collapsed
    ? cn(PAGE_TITLE_CLASSES, 'max-sm:line-clamp-1')
    : PAGE_TITLE_CLASSES

  return (
    <div
      className={cn(
        'mb-0',
        'w-full overflow-clip',
        fixed && 'sticky top-[0rem] z-20',
        className,
      )}
    >
      {/* Enhanced header with simplified glass effect */}
      <div
        className={cn(
          // No hairline under the header: the content below is its own white
          // sheet on the canvas, so the tonal step already separates them.
          'bg-canvas/90 transition-all duration-300',
          background && 'relative overflow-hidden',
          fixed && 'bg-canvas',
          fixed && isCondensed && !flat && 'shadow-sm',
          collapsed && !flat && 'shadow-sm',
        )}
      >
        {background && (
          <div className="pointer-events-none absolute inset-0" aria-hidden>
            {background}
          </div>
        )}
        <div
          className={cn(
            'relative mx-auto w-full transition-all duration-300',
            containerClasses,
            // With tabs below, the title row keeps no bottom padding: the
            // tab row supplies the space, so the two never stack gaps.
            // Collapsed with inline tabs, the active tab's underline sits on
            // the bottom edge, but the top keeps 6px so the 36px button
            // islands don't touch the sheet's top edge while scrolling.
            hasInlineTabs
              ? 'pb-0 pt-1.5'
              : isCondensed && fixed
                ? hasTabs
                  ? 'pb-0 pt-1.5'
                  : 'py-1.5'
                : isCondensed
                  ? hasTabs
                    ? 'pb-0 pt-2.5'
                    : 'py-2.5'
                  : hasTabs
                    ? 'pb-0 pt-3 sm:pt-4'
                    : 'py-3 sm:py-4',
          )}
        >
          {isLoading ? (
            <PageHeaderSkeleton
              condensed={isCondensed}
              hasAvatar={
                !!(
                  moduleType &&
                  entityData &&
                  (moduleType === ModuleTypeEnum.ENTITY ||
                    moduleType === ModuleTypeEnum.PROJECT)
                )
              }
              hasDescription={!!description}
              hasActions={!!(actions || menu)}
              isDisplay={titleSize === 'display'}
              hasBreadcrumbs={!!(breadcrumbs && breadcrumbs.length > 0)}
            />
          ) : isCondensed ? (
            // Condensed two-line layout (one line when scroll-collapsed)
            <div className="transition-tonal space-y-1 duration-300">
              {/* First line: Breadcrumbs and Date (dropped when collapsed) */}
              {!collapsed && (
                <div className="transition-tonal flex flex-wrap items-center gap-2 duration-300">
                  <div className="flex flex-wrap items-center gap-4">
                    {/* Breadcrumbs */}
                    {breadcrumbs && breadcrumbs.length > 0 && (
                      <Breadcrumb>
                        <BreadcrumbList>
                          {breadcrumbs.map((item, index) => (
                            <Fragment key={index}>
                              <BreadcrumbItem>
                                {item.current || !item.href ? (
                                  <BreadcrumbPage className="text-xs">
                                    {item.label}
                                  </BreadcrumbPage>
                                ) : (
                                  <BreadcrumbLink
                                    href={item.href}
                                    className="text-xs"
                                  >
                                    {item.label}
                                  </BreadcrumbLink>
                                )}
                              </BreadcrumbItem>
                              {index < breadcrumbs.length - 1 && (
                                <BreadcrumbSeparator />
                              )}
                            </Fragment>
                          ))}
                        </BreadcrumbList>
                      </Breadcrumb>
                    )}
                  </div>

                  {/* Actions moved to second line in condensed mode */}
                </div>
              )}

              {/* Second line: Title and Actions. They share one row at every
                  width. On phones the ⋯ menu stays top right beside the title
                  and the other actions take the next row; without a menu the
                  actions only wrap when they don't fit beside the title. */}
              <div
                className={cn(
                  'transition-tonal flex flex-wrap items-center justify-between duration-300',
                  collapsed
                    ? 'gap-x-4 gap-y-1 lg:flex-nowrap'
                    : 'gap-x-4 gap-y-2 sm:flex-nowrap',
                )}
              >
                <div
                  className={cn(
                    // Phones size the title by its text (flex-auto) so the
                    // actions wrap instead of crushing it; wider screens let
                    // the title take the remaining space.
                    'transition-tonal min-w-0 flex-1 duration-300',
                    !menu && 'max-sm:flex-auto',
                    hasInlineTabs && 'lg:max-w-[40%] lg:flex-none',
                  )}
                >
                  {/* Title with optional back link */}
                  {greeting ? (
                    <h1 className="text-consistent truncate text-lg font-semibold tracking-tight text-foreground/95 sm:text-xl">
                      {/* The same greeting as the full header: in the
                          viewer's profile timezone, not the machine clock,
                          so scrolling never flips "evening" to "afternoon". */}
                      {greetingText},{' '}
                      <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                        {firstName}
                      </span>
                      .
                    </h1>
                  ) : (
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      {backLink && (
                        <Link
                          to={backLink.href}
                          className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                          title={`Back to ${backLink.label}`}
                        >
                          <span>{backLink.label}</span>
                          <span className="text-border">/</span>
                        </Link>
                      )}
                      {detail
                        ? renderDetailTitle(
                            detail,
                            moduleType,
                            entityData,
                            onEntityClick,
                            onLogoChange,
                            condensedTitleClasses,
                            collapsed ? 'xs' : 'md',
                          )
                        : renderTitleWithAvatar(
                            title,
                            titleSuffix,
                            moduleType,
                            entityData,
                            condensedTitleClasses,
                            onEntityClick,
                            onLogoChange,
                            Icon,
                            collapsed ? 'xs' : 'md',
                          )}
                    </div>
                  )}

                  {/* Description is hidden in condensed mode */}
                </div>

                {hasInlineTabs && (
                  <div className="hidden min-w-0 flex-1 self-stretch lg:flex lg:items-end">
                    <PageTabs tabs={tabs} />
                  </div>
                )}

                {/* min-w-0 (not flex-shrink-0): a wide action cluster must be
                    allowed to shrink so its own flex-wrap can engage —
                    otherwise it overflows across the title on narrow windows.
                    On phones the group dissolves (contents) so the menu and
                    the actions place themselves in the title row. */}
                {(actions || menu) && (
                  <div className="flex min-w-0 flex-wrap items-center gap-2 max-sm:contents">
                    {actions && (
                      <div
                        className={cn(
                          'flex min-w-0 flex-wrap items-center gap-2',
                          menu && 'max-sm:order-last max-sm:basis-full',
                        )}
                      >
                        {actions}
                      </div>
                    )}
                    {menu && <div className="shrink-0">{menu}</div>}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Original expanded layout
            // Below md the title and the ⋯ menu share the first row and the
            // other actions take a full row of their own.
            <div className="flex flex-wrap items-start gap-3 md:flex-nowrap md:items-center md:justify-between md:gap-6">
              <div className="min-w-0 flex-1">
                {/* Breadcrumbs */}
                {breadcrumbs && breadcrumbs.length > 0 && (
                  <div className="mb-2 sm:mb-4">
                    <Breadcrumb>
                      <BreadcrumbList>
                        {breadcrumbs.map((item, index) => (
                          <Fragment key={index}>
                            <BreadcrumbItem>
                              {item.current || !item.href ? (
                                <BreadcrumbPage className="text-sm">
                                  {item.label}
                                </BreadcrumbPage>
                              ) : (
                                <BreadcrumbLink
                                  href={item.href}
                                  className="text-sm"
                                >
                                  {item.label}
                                </BreadcrumbLink>
                              )}
                            </BreadcrumbItem>
                            {index < breadcrumbs.length - 1 && (
                              <BreadcrumbSeparator />
                            )}
                          </Fragment>
                        ))}
                      </BreadcrumbList>
                    </Breadcrumb>
                  </div>
                )}

                {/* Enhanced title section */}
                <div className="space-y-2 sm:space-y-3">
                  {greeting && (
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                      <h1 className="text-display text-consistent break-words text-foreground/95">
                        {greetingText},{' '}
                        <span className="modern-greeting">{firstName}</span>.
                      </h1>
                    </div>
                  )}

                  {/* Page title with optional back link */}
                  {!greeting && (
                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                      {backLink && (
                        <Link
                          to={backLink.href}
                          className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                          title={`Back to ${backLink.label}`}
                        >
                          <span>{backLink.label}</span>
                          <span className="text-border">/</span>
                        </Link>
                      )}
                      {detail
                        ? renderDetailTitle(
                            detail,
                            moduleType,
                            entityData,
                            onEntityClick,
                            onLogoChange,
                          )
                        : renderTitleWithAvatar(
                            title,
                            titleSuffix,
                            moduleType,
                            entityData,
                            titleSize === 'display'
                              ? 'text-display text-foreground/95 text-consistent'
                              : PAGE_TITLE_CLASSES,
                            onEntityClick,
                            onLogoChange,
                            Icon,
                          )}
                    </div>
                  )}

                  {/* Enhanced description styling */}
                  {description && (
                    <div className="max-w-3xl text-sm leading-relaxed text-muted-foreground/90 sm:text-base">
                      {description}
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons section */}
              {(actions || menu) && (
                <div className="flex flex-shrink-0 flex-wrap items-center gap-2 max-md:contents">
                  {actions && (
                    <div className="flex flex-wrap items-center gap-2 max-md:order-last max-md:basis-full">
                      {actions}
                    </div>
                  )}
                  {menu && <div className="shrink-0">{menu}</div>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Full-width hero content between the title row and tabs, rendered over `background` */}
        {hero && !collapsed && (
          <div className="relative w-full min-w-0">{hero}</div>
        )}

        {/* Strip row (e.g. a stage gauge): its own line in the header gutter, above the tabs */}
        {strip && !collapsed && (
          <div className="relative w-full min-w-0">
            <div className={cn('mx-auto w-full min-w-0', containerClasses)}>
              <div className={cn('min-w-0', isCondensed ? 'py-2' : 'py-3')}>
                {strip}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced tabs navigation */}
        {hasTabs && (
          <div
            className={cn(
              'relative w-full min-w-0 overflow-hidden',
              hasInlineTabs && 'lg:hidden',
              // Over a background cover, give the tab row its own translucent
              // surface so tab labels stay readable on any image
              background && 'border-t border-border/30 bg-background',
            )}
          >
            <div
              className={cn(
                'mx-auto w-full min-w-0 transition-all duration-300',
                containerClasses,
              )}
            >
              <div
                className={cn(
                  'min-w-0',
                  isCondensed && fixed
                    ? 'mt-0'
                    : isCondensed
                      ? 'mt-0.5'
                      : 'mt-1',
                )}
              >
                {/* Phones: the strip bleeds to the screen edges so a tab
                    cut off by the viewport reads as swipeable, while the
                    first label still lines up with the title's gutter. */}
                <PageTabs
                  tabs={tabs}
                  className="max-sm:-mx-4 max-sm:w-[calc(100%+2rem)] max-sm:max-w-none max-sm:px-2"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced children section */}
      {children && (
        <div className="border-b border-border/20 bg-background/80 dark:border-border/30 dark:bg-[hsl(220_15%_9%)]">
          <div
            className={cn(
              'mx-auto w-full py-4 transition-all duration-300 sm:py-6',
              containerClasses,
            )}
          >
            <div className="rounded-xl border border-border/40 bg-background/95 p-3 shadow-sm dark:border-border/50 dark:bg-[hsl(220_15%_9%)] sm:p-4">
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
